import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { ArtRenderer, CoinView } from './model';
import { recentCallOrder } from './arenaLists';
import {
  MOBILE_RAIL_LIMIT,
  mobileRailPose,
  mobileRailWidth,
} from './mobileLayout';
import { useCoinModal } from './CoinModal';
import { RAIL_LIMIT } from './railMotion';
import { AnimatedNumber } from '../AnimatedNumber';
import { money } from './parts';
import { TrendGlyph } from './icons';

export function NavigationDock({
  children,
  coins = [],
  art,
  view,
  onPredictions,
  filters,
}: {
  children: ReactNode;
  coins?: CoinView[];
  art?: ArtRenderer;
  view: string;
  onPredictions: () => void;
  filters?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(0);
  const [scroll, setScroll] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const railId = useId();
  const details = useCoinModal();
  const ids = coins.filter((coin) => coin.choice).map((coin) => coin.id);
  const [previous, setPrevious] = useState(ids);
  const ordered = recentCallOrder(previous, ids);
  if (
    ordered.length !== previous.length ||
    ordered.some((id, i) => id !== previous[i])
  )
    setPrevious(ordered);
  const mine = ordered.slice(0, RAIL_LIMIT).flatMap((id) => {
    const coin = coins.find((coin) => coin.id === id);
    return coin ? [coin] : [];
  });
  const expanded = open && mine.length > 0;
  const hasCalls = mine.length > 0;
  const contentWidth = mobileRailWidth(width, mine.length);

  useLayoutEffect(() => {
    const node = root.current!;
    const measure = () => setWidth(node.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    // Keep the history end visible on resize/new arrivals; never reset scroll during a flight.
    const node = scroller.current;
    if (!node) return;
    node.scrollLeft = Math.max(0, contentWidth - width);
    setScroll(node.scrollLeft);
  }, [contentWidth, width]);
  useEffect(() => {
    setOpen(false);
  }, [view]);
  useEffect(() => {
    if (!hasCalls) setOpen(false);
  }, [hasCalls]);
  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 760px)');
    const closeOnDesktop = () => {
      if (!mobile.matches) setOpen(false);
    };
    mobile.addEventListener('change', closeOnDesktop);
    return () => mobile.removeEventListener('change', closeOnDesktop);
  }, []);
  useEffect(() => {
    if (!expanded || details?.isOpen) return;
    const close = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggle.current?.focus();
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [expanded, details?.isOpen]);
  return (
    <div
      ref={root}
      className={
        'navigation-dock' +
        (expanded ? ' is-open' : '') +
        (hasCalls ? ' has-calls' : '') +
        (filters ? ' has-filters' : '')
      }
    >
      {filters && <div className="mobile-dock-filters">{filters}</div>}
      <div className="navigation-dock-bar">
        {children}
        <div className="mobile-rail-slot" aria-hidden={!hasCalls}>
          <button
            ref={toggle}
            type="button"
            className="mobile-rail-toggle"
            aria-label={
              expanded ? 'Close your calls' : 'Open your last four calls'
            }
            aria-expanded={expanded}
            aria-controls={railId}
            disabled={!mine.length}
            tabIndex={hasCalls ? 0 : -1}
            onClick={() => setOpen((value) => !value)}
          >
            <img
              className="mobile-rail-close"
              src="/images/mobile-calls-close.svg"
              alt=""
            />
          </button>
        </div>
      </div>
      <div
        className="mobile-calls-viewport"
        ref={scroller}
        onScroll={(event) => setScroll(event.currentTarget.scrollLeft)}
      >
        <ul
          id={railId}
          className="mobile-calls"
          aria-label="Your recent calls"
          aria-hidden={!expanded}
          style={{ width: contentWidth }}
        >
          {mine.map((coin, index) => {
            const pose = mobileRailPose(
              width,
              index,
              expanded,
              mine.length,
              scroll,
            );
            const visible = expanded || index < MOBILE_RAIL_LIMIT;
            return (
              <li
                key={coin.id}
                data-coin-id={coin.id}
                style={
                  {
                    '--flight-x': pose.x + 'px',
                    '--flight-y': pose.y + 'px',
                    '--flight-scale': pose.scale,
                    '--flight-delay':
                      (expanded ? index : Math.min(index, 3)) * 28 + 'ms',
                  } as CSSProperties
                }
                data-visible={visible}
              >
                <div className="mobile-call-rise">
                  <div className="mobile-call-size">
                    <div className="mobile-call-arrival">
                      <button
                        type="button"
                        tabIndex={expanded ? 0 : -1}
                        aria-label={`${coin.name}, your ${coin.choice === 'SURVIVES' ? 'RUN' : 'DIE'} call`}
                        aria-haspopup="dialog"
                        disabled={!details}
                        onClick={(event) =>
                          details?.open(coin, event.currentTarget)
                        }
                      >
                        {art?.(coin, 'mobile-call-art')}
                        <span className="mobile-call-cap">
                          <AnimatedNumber value={money(coin.marketCap)} />
                        </span>
                        <span
                          className={
                            'mobile-call-side ' +
                            (coin.changePercent === null
                              ? 'is-unknown'
                              : coin.changePercent >= 0
                                ? 'is-up'
                                : 'is-down')
                          }
                        >
                          {coin.changePercent !== null && <TrendGlyph />}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          <li
            className="mobile-call-history"
            style={{
              left:
                (contentWidth - ((mine.length + 1) * 60 - 8)) / 2 +
                mine.length * 60,
            }}
          >
            <button
              type="button"
              aria-label="Your predictions"
              tabIndex={expanded ? 0 : -1}
              onClick={() => {
                setOpen(false);
                onPredictions();
              }}
            >
              <img src="/images/mobile-calls-history.svg" alt="" />
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}
